/**
 * How busy the roads are by hour and day of the week.
 *
 * Traffic counts everywhere have the same shape. On a weekday there is a
 * morning peak around half past seven, a dip through the late morning, a
 * longer and higher afternoon peak from three to six, and next to nothing
 * between midnight and five. Friday's afternoon starts earlier and runs
 * busier. Saturday rises late to a broad midday hump. Sunday is later and
 * quieter still, with people driving home through the afternoon.
 *
 * The curves below are hourly shares of a day's traffic, shaped after typical
 * hourly distributions published from Swedish and European road counts
 * (Trafikverket's measuring stations among them). They are not one road's
 * real numbers. Multiplied by how a day compares with an ordinary weekday,
 * they give `trafficLevel`: 1 for an ordinary weekday midday, about 1.5 at
 * the evening peak and under 0.1 in the small hours.
 */

/** Percent of the day's traffic in each hour, 0..23. Each row sums to about 100. */
const WEEKDAY = [0.8, 0.5, 0.4, 0.4, 0.7, 1.8, 4.5, 7.5, 6.8, 5.3, 5.0, 5.3, 5.5, 5.6, 6.0, 7.0, 8.0, 7.6, 5.8, 4.3, 3.3, 2.7, 2.0, 1.3];
const FRIDAY = [0.8, 0.5, 0.4, 0.4, 0.6, 1.6, 4.0, 6.6, 6.0, 5.0, 5.0, 5.4, 5.8, 6.2, 7.0, 8.0, 8.1, 7.2, 5.6, 4.2, 3.4, 2.8, 2.3, 1.7];
const SATURDAY = [1.5, 1.0, 0.8, 0.6, 0.6, 1.0, 1.8, 2.8, 4.0, 5.4, 6.6, 7.2, 7.3, 7.1, 6.9, 6.7, 6.4, 6.0, 5.3, 4.4, 3.6, 3.1, 2.6, 2.0];
const SUNDAY = [1.8, 1.2, 0.9, 0.6, 0.5, 0.7, 1.1, 1.8, 2.8, 4.2, 5.6, 6.6, 7.0, 7.2, 7.3, 7.4, 7.4, 7.0, 6.2, 5.2, 4.3, 3.4, 2.4, 1.5];

/** A day's traffic against an ordinary weekday's, Sunday first as `getUTCDay` counts. */
const DAY_VOLUME = [0.72, 1, 1, 1, 1, 1.06, 0.82];
const DAY_CURVE = [SUNDAY, WEEKDAY, WEEKDAY, WEEKDAY, WEEKDAY, FRIDAY, SATURDAY];

/** The share an ordinary weekday's midday hour carries: what level 1 means. */
const MIDDAY = WEEKDAY[12];

/**
 * How busy the roads are at `hour` (0..24, fractional) on `weekday` (0 is
 * Sunday), against an ordinary weekday midday. Hours blend into each other,
 * and midnight blends into the next day.
 */
export function trafficLevel(hour: number, weekday: number): number {
  const h = ((hour % 24) + 24) % 24;
  // Each hour's share sits at its middle: 07:30 is the 7 o'clock hour's.
  const t = h - 0.5;
  const day = ((weekday % 7) + 7) % 7;
  const at = (d: number, i: number) => DAY_CURVE[d][i] * DAY_VOLUME[d];
  let a: number, b: number;
  if (t < 0) {
    a = at((day + 6) % 7, 23);
    b = at(day, 0);
  } else if (t >= 23) {
    a = at(day, 23);
    b = at((day + 1) % 7, 0);
  } else {
    a = at(day, Math.floor(t));
    b = at(day, Math.floor(t) + 1);
  }
  const f = t - Math.floor(t);
  return (a + (b - a) * f) / MIDDAY;
}

/**
 * The time on the clocks at a place: its hour and day of the week.
 *
 * Rush hour keeps the clock's time, not the sun's, so this is the civil time
 * the place most likely keeps. That is the standard zone of its longitude,
 * plus an hour of summer time where it is kept: Europe, North America, and in
 * the south Chile and south-east Australia, by the European dates mirrored.
 * Real zone borders wander, so this can be an hour out at the edges, which
 * the traffic does not much mind.
 */
export function localTime(date: Date, lat: number, lon: number): { hour: number; weekday: number } {
  let offset = Math.round(lon / 15);
  if (keepsSummerTime(lat, lon) && summerTime(date, lat)) offset += 1;
  const shifted = new Date(date.getTime() + offset * 3_600_000);
  const hour = shifted.getUTCHours() + shifted.getUTCMinutes() / 60 + shifted.getUTCSeconds() / 3600;
  return { hour, weekday: shifted.getUTCDay() };
}

/** Roughly where clocks go forward in summer. */
function keepsSummerTime(lat: number, lon: number): boolean {
  if (lat > 30) return (lon > -170 && lon < -50) || (lon > -25 && lon < 45);
  if (lat < -30) return (lon > 135 && lon < 180) || (lon > -80 && lon < -65);
  return false;
}

/** Whether summer time is likely in force: end of March to end of October in the north, the reverse in the south. */
function summerTime(date: Date, lat: number): boolean {
  const year = date.getUTCFullYear();
  const start = lastSunday(year, 2);
  const end = lastSunday(year, 9);
  const northern = date.getTime() >= start && date.getTime() < end;
  return lat > 0 ? northern : !northern;
}

/** 01:00 UTC on the last Sunday of a month, ms: when Europe changes its clocks. */
function lastSunday(year: number, month: number): number {
  const last = new Date(Date.UTC(year, month + 1, 0, 1));
  return last.getTime() - last.getUTCDay() * 86_400_000;
}
